import {
  DependencyList, useEffect, useState,
} from 'react';
import { ClientStatus } from '../../../model/ClientStatus';
import { ClientResponse } from '../../../model/ClientResponse';
import useClientStatusContext from '../context/useClientStatusContext';
import { ClientStatusError } from '../../../model/ClientStatusError';

interface ReturnType<T> {
  wrapClient(...args): Promise<ClientResponse<T>>;
  data: T | null | undefined
  status: ClientStatus;
}

type ClientCall<T> = (...args) => Promise<ClientResponse<T>>;

export default <T> (client: ClientCall<T>, deps: DependencyList = []): ReturnType<T> => {
  const { handleStatus } = useClientStatusContext();
  const [status, setStatus] = useState<ClientStatus>({
    failed: false,
    finished: false,
    generalErrors: [],
    fieldErrors: [],
    loading: false,
    responseCode: undefined,
  });
  const [data, setData] = useState<T | null | undefined>(null);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const wrapClient = async (...args) => {
    setStatus(prevState => ({ ...prevState, loading: true }));

    try {
      const response = await client(...args);

      const newStatus = {
        responseCode: response.responseCode,
        loading: false,
        finished: true,
        generalErrors: [],
        fieldErrors: [],
        failed: false,
      };

      setData(response.data);
      setStatus(newStatus);
      handleStatus(newStatus);

      return Promise.resolve(response);
    } catch (e) {
      const generalResponseErrors: ClientStatusError[] = [];
      const fieldResponseErrors: ClientStatusError[] = [];

      if (e.response == null) {
        const newStatus = {
          responseCode: 503,
          loading: false,
          finished: true,
          generalErrors: generalResponseErrors,
          fieldErrors: fieldResponseErrors,
          failed: true,
        };

        setData(null);
        setStatus(newStatus);
        handleStatus(newStatus);

        return Promise.reject();
      }

      const { data: responseData, status: statusCode } = e.response;

      responseData?.generalErrors?.forEach(error => generalResponseErrors.push(
        { message: error },
      ));

      responseData?.fieldErrors?.forEach(error => fieldResponseErrors.push(
        { message: error.message, fieldName: error.field },
      ));

      const newStatus = {
        responseCode: statusCode,
        loading: false,
        finished: true,
        generalErrors: generalResponseErrors,
        fieldErrors: fieldResponseErrors,
        failed: true,
      };

      setData(null);
      setStatus(newStatus);
      handleStatus(newStatus);

      return Promise.reject();
    }
  };

  useEffect(() => {
    wrapClient()
      .catch(() => { /* noop */ });
    return () => {
      setStatus(prevState => ({ ...prevState, loading: false }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    wrapClient, data, status,
  };
};
